import { useRef, useState } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { scannerApi } from '../../services/api';
import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';

/**
 * Bouton micro réutilisable (dictée vocale).
 * - Clic court : démarre / arrête l'enregistrement.
 * - Clic maintenu (> 300 ms) : enregistre tant que le bouton est pressé, s'arrête au relâché.
 * À l'arrêt, l'audio est transcrit (Whisper) puis renvoyé via onTranscribe(text).
 */
export function VoiceDictateButton({ onTranscribe, size = 'sm', showLabel = true, testId = 'voice-dictate' }) {
  const { recording, start, stop } = useVoiceRecorder();
  const [busy, setBusy] = useState(false);
  const hold = useRef({ timer: null, held: false });

  const finish = async () => {
    const res = await stop();
    if (!res?.blob) return;
    setBusy(true);
    try {
      const n = await scannerApi.transcribe(res.blob, res.filename);
      if (n?.transcription) { await onTranscribe(n.transcription); toast.success('Note vocale transcrite'); }
      else toast.info('Aucune parole détectée');
    } catch { toast.error('Transcription impossible'); }
    finally { setBusy(false); }
  };
  const down = () => {
    hold.current.held = false;
    hold.current.timer = setTimeout(async () => { hold.current.held = true; if (!recording) await start(); }, 300);
  };
  const up = async () => {
    clearTimeout(hold.current.timer);
    if (hold.current.held) await finish();
    else if (recording) await finish();
    else await start();
    hold.current.held = false;
  };

  const dim = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const ic = size === 'sm' ? 'w-4 h-4' : 'w-[18px] h-[18px]';
  const active = recording
    ? 'bg-red-600 text-white ring-2 ring-red-300 scale-110 animate-pulse'
    : busy
      ? 'bg-gold text-white scale-105'
      : 'text-gold700 hover:bg-gold100 hover:scale-105 active:scale-95';
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <button type="button" data-testid={testId}
        onPointerDown={(e) => { e.preventDefault(); down(); }}
        onPointerUp={(e) => { e.preventDefault(); up(); }}
        onPointerLeave={() => { if (recording && hold.current.held) up(); }}
        title="Note vocale — clic pour démarrer/arrêter, ou maintenez pour enregistrer"
        aria-label="Dicter une note vocale"
        className={`inline-flex items-center justify-center ${dim} rounded-full transition will-change-transform ${active}`}>
        {busy ? <Loader2 className={`${ic} animate-spin`} /> : <Mic className={ic} />}
      </button>
      {recording && showLabel && <span className="text-[11px] text-red-600 font-sans normal-case">● enregistrement…</span>}
    </span>
  );
}
